import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { MagnifyingGlass, FileText, X, WarningCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multi-select';
import { StatusBadge } from '@/components/enterprise/StatusBadge';
import { useAuth } from '@/auth/AuthContext';
import { engApi } from '../engApi';
import { devApi } from './devApi';
import { todayJst } from '@/utils/jstDate';

/** 証跡（写真・試験報告書など）をドキュメントアプリから選ぶ。実体は複製しない。 */
export const EvidencePicker = ({ picked, onChange, testid }) => {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const search = async (v) => {
    setQ(v);
    if (!v.trim()) { setHits([]); return; }
    try { setHits(await devApi.searchDocs(v) || []); } catch (_e) { setHits([]); }
  };
  return (
    <div data-testid={testid}>
      <Label className="text-xs font-semibold text-zinc-700">証跡（写真・報告書など・任意）</Label>
      <div className="relative">
        <MagnifyingGlass size={15} className="absolute left-2.5 top-2.5 text-zinc-500" />
        <Input value={q} onChange={(e) => search(e.target.value)} className="pl-8"
          placeholder="ドキュメントアプリから探す" data-testid={`${testid}-search`} />
      </div>
      {hits.length > 0 && (
        <div className="mt-2 max-h-36 divide-y divide-zinc-200 overflow-y-auto rounded-md border border-zinc-300">
          {hits.map((d) => (
            <button key={d.id} type="button" data-testid={`${testid}-option-${d.id}`}
              onClick={() => {
                if (!picked.some((p) => p.document_id === d.id)) {
                  onChange([...picked, { document_id: d.id, title: d.title,
                    document_number: d.document_number, page: null }]);
                }
                setQ(''); setHits([]);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-teal-50/60">
              <FileText size={14} className="shrink-0 text-zinc-500" />
              <span className="min-w-0 flex-1 truncate text-sm">{d.title}</span>
            </button>
          ))}
        </div>
      )}
      {picked.length > 0 && (
        <div className="mt-2 space-y-1">
          {picked.map((p, i) => (
            <div key={p.document_id} className="flex items-center gap-2 rounded-md border border-teal-300 bg-teal-50 px-2 py-1">
              <FileText size={12} className="text-teal-700" />
              <span className="min-w-0 flex-1 truncate text-xs text-teal-900">{p.title}</span>
              <Input type="number" min={1} value={p.page || ''} placeholder="p."
                className="h-6 w-16 text-xs"
                onChange={(e) => onChange(picked.map((x, j) => j === i
                  ? { ...x, page: e.target.value ? Number(e.target.value) : null } : x))}
                data-testid={`${testid}-page-${i}`} />
              <button type="button" aria-label="外す" className="text-teal-700 hover:text-rose-600"
                onClick={() => onChange(picked.filter((_x, j) => j !== i))}><X size={11} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/** 試作を登録 — 4項目だけ。名称は 試作N号 の自動採番、図番メモは改訂の流れが持つ。 */
export const CreatePrototypeDialog = ({ devId, open, onOpenChange, onDone, rebuildOf }) => {
  const { user } = useAuth();
  const [qty, setQty] = useState('1');
  const [date, setDate] = useState('');
  const [builders, setBuilders] = useState([]);
  const [notes, setNotes] = useState('');
  const [people, setPeople] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQty('1');
    setDate(todayJst());
    setNotes('');
    engApi.members().then((d) => {
      const rows = d.users || [];
      // 同姓同名がいる場合だけメールを添えてラベルを一意にする（IDは裏で持つ）
      const dup = new Set(rows.map((u) => u.name).filter((n, i, a) => a.indexOf(n) !== i));
      setPeople(rows.map((u) => ({
        id: u.id,
        label: dup.has(u.name) ? `${u.name}（${u.email}）` : (u.name || u.email),
      })));
      const me = rows.find((u) => u.id === user?.id);
      setBuilders(me ? [dup.has(me.name) ? `${me.name}（${me.email}）` : (me.name || me.email)] : []);
    }).catch(() => setPeople([]));
  }, [open, user?.id]);

  const submit = async () => {
    const n = Number(qty);
    if (!Number.isInteger(n) || n < 1 || n > 999) {
      toast.error('製作台数は1〜999の整数で入力してください'); return;
    }
    setBusy(true);
    try {
      const ids = builders.map((l) => people.find((p) => p.label === l)?.id).filter(Boolean);
      await devApi.createPrototype(devId, {
        build_quantity: n,
        build_date: date || null,
        builder_user_ids: ids,
        notes: notes.trim() || null,
        rebuild_of: rebuildOf?.id || null,
      });
      toast.success('試作を登録しました（在庫はまだ動いていません）');
      onOpenChange(false); onDone?.();
    } catch (e) {
      const det = e.response?.data?.detail;
      if (det && typeof det === 'object') {
        toast.error(`${det.message}\n${(det.blockers || []).map((b) => `・${b}`).join('\n')}`,
          { duration: 8000 });
      } else { toast.error(det || '登録できませんでした'); }
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white" data-testid="dev-proto-create-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">試作を登録</DialogTitle>
          <DialogDescription className="text-sm">
            名称は自動で付きます。登録だけでは在庫は動きません。
            {rebuildOf ? `（${rebuildOf.name} の作り直しとして記録します）` : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold text-zinc-700">製作台数（必須）</Label>
            <Input type="number" min={1} max={999} step={1} value={qty}
              onChange={(e) => setQty(e.target.value)} className="h-10 mt-1"
              data-testid="dev-proto-create-qty" />
            <p className="mt-1 text-[11px] text-zinc-500">
              必要部品の数量（1台あたり × 台数）の判定に使われます。
            </p>
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-700">製作日</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="h-10 mt-1" data-testid="dev-proto-create-date" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-700">製作者（複数選択可）</Label>
            <div className="mt-1">
              <MultiSelect options={people.map((p) => p.label)} selected={builders}
                onChange={setBuilders} placeholder="選択してください"
                testId="dev-proto-create-builders" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-700">備考（任意）</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="例: 治具は既存のものを流用" data-testid="dev-proto-create-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>閉じる</Button>
          <Button onClick={submit} disabled={busy} data-testid="dev-proto-create-submit">
            {busy ? '登録中…' : '試作を登録する'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** 試作を取消 — 記録・投入部品・在庫・検証はすべて残る（消去ではない）。 */
export const CancelPrototypeDialog = ({ devId, proto, reasons, open, onOpenChange, onDone }) => {
  const [code, setCode] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!code) { toast.error('取消の理由を選んでください'); return; }
    if (!reason.trim()) { toast.error('理由を入力してください'); return; }
    setBusy(true);
    try {
      const d = await devApi.cancelPrototype(devId, proto.id, { reason_code: code, reason: reason.trim() });
      toast.success('試作を取消しました（記録と在庫はそのまま残ります）');
      setCode(''); setReason('');
      onOpenChange(false); onDone?.(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || '取消できませんでした');
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white" data-testid="dev-proto-cancel-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">試作を取消 — {proto?.name}</DialogTitle>
          <DialogDescription className="text-sm">
            記録・投入した部品・消費した在庫・検証記録はすべて残ります。在庫は動きません。
            未処置の部品は取消後も処置の記録が必要です。
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold text-zinc-700">取消の理由（必須）</Label>
            <Select value={code} onValueChange={setCode}>
              <SelectTrigger data-testid="dev-proto-cancel-code" className="mt-1">
                <SelectValue placeholder="選択してください" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {(reasons || []).map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="mt-1 text-[11px] text-zinc-500">
              技術的な内容は検証記録に残すため、ここでは重複して書かなくて構いません。
            </p>
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-700">経緯（必須）</Label>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="例: 客先仕様が変わり、この構成では成立しないため中止"
              data-testid="dev-proto-cancel-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>閉じる</Button>
          <Button onClick={submit} disabled={busy} data-testid="dev-proto-cancel-submit">
            {busy ? '取消中…' : '試作を取消す'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** 部品を投入 — 計画から数量を引き継ぎ、確定時に初めて在庫が動く。 */
export const InstallPartsDialog = ({ devId, proto, open, onOpenChange, onDone }) => {
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !proto) return;
    devApi.installCandidates(devId, proto.id).then((d) => {
      setRows(d.candidates || []);
      const next = {};
      (d.candidates || []).forEach((c) => {
        if (c.suggested_qty > 0) next[c.part_id] = c.suggested_qty;
      });
      setSel(next);
    }).catch(() => toast.error('投入候補を読み込めませんでした'));
  }, [open, devId, proto]);

  const submit = async () => {
    const lines = Object.entries(sel).filter(([, q]) => Number(q) > 0)
      .map(([part_id, qty]) => ({ part_id, qty: Number(qty) }));
    if (lines.length === 0) { toast.error('投入する部品と数量を指定してください'); return; }
    setBusy(true);
    try {
      const d = await devApi.installParts(devId, proto.id, lines);
      toast.success('部品を投入しました（在庫に反映されました）');
      onOpenChange(false); onDone?.(d);
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === 'object'
        ? `${det.message}（必要 ${det.requested} / 在庫 ${det.available} / 不足 ${det.shortage}）${det.hint || ''}`
        : det || '投入できませんでした');
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-white" data-testid="dev-install-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">部品を投入 — {proto?.name}</DialogTitle>
          <DialogDescription className="text-sm">
            計画した必要部品から数量を引き継いでいます。確定すると在庫が減ります。在庫が足りない部品は投入できません。
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-80 overflow-y-auto rounded-md border border-zinc-300">
          <table className="w-full text-sm">
            <thead className="bg-zinc-100 text-left text-xs text-zinc-600">
              <tr>
                <th className="px-2 py-1.5">部品</th>
                <th className="px-2 py-1.5">図番 / 版</th>
                <th className="px-2 py-1.5 text-right">必要</th>
                <th className="px-2 py-1.5 text-right">投入済み</th>
                <th className="px-2 py-1.5 text-right">在庫</th>
                <th className="px-2 py-1.5 text-right">今回投入</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {rows.map((c) => (
                <tr key={c.part_id} data-testid={`dev-install-row-${c.part_id}`}>
                  <td className="px-2 py-1.5">
                    {c.name}
                    {c.short && (
                      <StatusBadge tone="danger" className="ml-1" testid={`dev-install-short-${c.part_id}`}>
                        <WarningCircle size={11} /> 在庫不足
                      </StatusBadge>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-xs text-zinc-600">
                    {c.drawing_no || '—'}{c.drawing_revision ? ` ${c.drawing_revision}` : ''}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{c.required}{c.uom}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{c.already_installed}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {c.available_known ? c.available : '—'}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <Input type="number" min={0} value={sel[c.part_id] ?? ''}
                      className="ml-auto h-7 w-20 text-sm"
                      onChange={(e) => setSel({ ...sel, [c.part_id]: e.target.value })}
                      data-testid={`dev-install-qty-${c.part_id}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>閉じる</Button>
          <Button onClick={submit} disabled={busy} data-testid="dev-install-submit">
            {busy ? '投入中…' : '投入を確定する'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** 部品の処置 — 返却 / スクラップ / 手直し / 保留 / 他案件で使用可（数量は分割できる）。 */
export const DispositionDialog = ({ devId, proto, row, types, categories, onClose, onDone }) => {
  const [type, setType] = useState('returned');
  const [qty, setQty] = useState(row?.remaining_qty || 1);
  const [reason, setReason] = useState('');
  const [cause, setCause] = useState('');
  const [evidence, setEvidence] = useState([]);
  const [busy, setBusy] = useState(false);
  // 捨てた・作り直したは失敗なので原因を必須にする。返却・他案件で使用可は材料が戻る
  // 良い結果、保留は結果が未確定なので求めない。
  const needsCause = ['scrapped', 'rework'].includes(type);

  const submit = async () => {
    if (!reason.trim()) { toast.error('理由を入力してください'); return; }
    if (needsCause && !cause) { toast.error('原因分類を選んでください'); return; }
    setBusy(true);
    try {
      const d = await devApi.disposePart(devId, proto.id, row.id, {
        type, qty: Number(qty), reason: reason.trim(),
        cause_category: needsCause ? cause : null, evidence });
      toast.success('処置を記録しました');
      onClose(); onDone?.(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || '記録できませんでした');
    }
    setBusy(false);
  };

  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-white" data-testid="dev-disposition-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">部品の処置 — {row?.name}</DialogTitle>
          <DialogDescription className="text-sm">
            投入した {row?.qty}{row?.uom} のうち、処置できる残りは {row?.remaining_qty}{row?.uom} です。
            返却・他案件で使用可は在庫に戻ります。スクラップは記録として必ず残ります。
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-zinc-700">処置</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="dev-disposition-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {(types || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-700">数量</Label>
              <Input type="number" min={0.0001} max={row?.remaining_qty} value={qty}
                onChange={(e) => setQty(e.target.value)} data-testid="dev-disposition-qty" />
            </div>
          </div>
          {needsCause && (
            <div className="rounded-md border border-amber-300 bg-amber-50/60 p-3"
              data-testid="dev-disposition-cause-block">
              <Label className="text-xs font-semibold text-zinc-700">原因分類（必須）</Label>
              <Select value={cause} onValueChange={setCause}>
                <SelectTrigger data-testid="dev-disposition-cause" className="mt-1">
                  <SelectValue placeholder="選択してください" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {(categories || []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-[11px] text-amber-900">
                検証の不具合分類と同じ区分です。あとから傾向を見られるように残します。
              </p>
            </div>
          )}
          <div>
            <Label className="text-xs font-semibold text-zinc-700">理由（必須）</Label>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="例: 穴位置が図面と合わないため修正して再利用"
              data-testid="dev-disposition-reason" />
          </div>
          <EvidencePicker picked={evidence} onChange={setEvidence} testid="dev-disposition-evidence" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>閉じる</Button>
          <Button onClick={submit} disabled={busy || !(Number(qty) > 0)}
            data-testid="dev-disposition-submit">{busy ? '記録中…' : '処置を記録する'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** 検証を記録する — 試作のステータスは判定に応じて自動で変わる。 */
export const ValidationDialog = ({ devId, proto, open, onOpenChange, results, categories, parts, onDone }) => {
  const [tested, setTested] = useState('');
  const [result, setResult] = useState('pass');
  const [issue, setIssue] = useState('');
  const [partId, setPartId] = useState('');
  const [testedAt, setTestedAt] = useState('');
  const [evidence, setEvidence] = useState([]);
  const [category, setCategory] = useState('');
  const [location, setLocation] = useState('');
  const [cause, setCause] = useState('');
  const [counter, setCounter] = useState('');
  const [recurrence, setRecurrence] = useState(false);
  const [busy, setBusy] = useState(false);
  const needsIssue = ['fail', 'needs_revision', 'pass_with_issues'].includes(result);

  const submit = async () => {
    if (!tested.trim()) { toast.error('検証した内容を入力してください'); return; }
    if (needsIssue && !issue.trim()) { toast.error('不具合・課題の内容を入力してください'); return; }
    if (needsIssue && !category) { toast.error('不具合の分類を選んでください'); return; }
    setBusy(true);
    try {
      const d = await devApi.createValidation(devId, proto.id, {
        tested_item: tested.trim(), result, issue_description: issue.trim() || null,
        part_id: partId || null, tested_at: testedAt || null, evidence,
        failure_category: needsIssue ? category : null,
        failure_location: location.trim() || null,
        cause: cause.trim() || null, countermeasure: counter.trim() || null,
        recurrence });
      toast.success('検証結果を記録しました');
      setTested(''); setIssue(''); setEvidence([]); setCause(''); setCounter('');
      setLocation(''); setCategory(''); setRecurrence(false);
      onOpenChange(false); onDone?.(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || '記録できませんでした');
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-white" data-testid="dev-validation-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">検証を記録 — {proto?.name}</DialogTitle>
          <DialogDescription className="text-sm">
            判定に応じて試作のステータスが変わります。不合格・図面修正が必要な場合は、次に改訂を作成できます。
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold text-zinc-700">検証した内容（必須）</Label>
            <Input value={tested} onChange={(e) => setTested(e.target.value)}
              placeholder="例: 温度均一性 ±3℃ の確認" data-testid="dev-validation-item" />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <Label className="text-xs font-semibold text-zinc-700">判定</Label>
              <Select value={result} onValueChange={setResult}>
                <SelectTrigger data-testid="dev-validation-result"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {(results || []).map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-700">実施日</Label>
              <Input type="date" value={testedAt} onChange={(e) => setTestedAt(e.target.value)}
                data-testid="dev-validation-date" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-700">対象の部品（任意）</Label>
              <Select value={partId} onValueChange={setPartId}>
                <SelectTrigger data-testid="dev-validation-part">
                  <SelectValue placeholder="選択しない" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {(parts || []).map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}{p.drawing_revision ? `（${p.drawing_revision}）` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-700">
              不具合・課題の内容{needsIssue ? '（必須）' : '（任意）'}
            </Label>
            <Textarea rows={3} value={issue} onChange={(e) => setIssue(e.target.value)}
              placeholder="例: ブラケットの穴位置が合わず取付できない"
              data-testid="dev-validation-issue" />
          </div>
          {needsIssue && (
            <div className="space-y-3 rounded-md border border-amber-300 bg-amber-50/60 p-3"
              data-testid="dev-validation-failure-block">
              <p className="text-xs font-bold text-amber-900">
                不具合データ（あとから傾向を見られるように残します）
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-xs font-semibold text-zinc-700">分類（必須）</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger data-testid="dev-validation-category">
                      <SelectValue placeholder="選択してください" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {(categories || []).map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-zinc-700">発生箇所</Label>
                  <Input value={location} onChange={(e) => setLocation(e.target.value)}
                    placeholder="例: ヒーター取付部" data-testid="dev-validation-location" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-700">原因</Label>
                <Textarea rows={2} value={cause} onChange={(e) => setCause(e.target.value)}
                  placeholder="例: 図面の穴ピッチが実機と合っていなかった"
                  data-testid="dev-validation-cause" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-700">対策</Label>
                <Textarea rows={2} value={counter} onChange={(e) => setCounter(e.target.value)}
                  placeholder="例: 図面を Rev C に改訂し、穴位置を修正して再製作"
                  data-testid="dev-validation-countermeasure" />
              </div>
              <label className="flex items-center gap-2 text-sm text-zinc-800">
                <Checkbox checked={recurrence} onCheckedChange={(v) => setRecurrence(!!v)}
                  data-testid="dev-validation-recurrence" />
                以前と同じ不具合の再発
              </label>
            </div>
          )}
          <EvidencePicker picked={evidence} onChange={setEvidence} testid="dev-validation-evidence" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>閉じる</Button>
          <Button onClick={submit} disabled={busy} data-testid="dev-validation-submit">
            {busy ? '記録中…' : '検証結果を記録する'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** 改訂を作成 — 旧版は履歴に残る（上書きしない）。 */
export const RevisionDialog = ({ devId, open, onOpenChange, parts, onDone }) => {
  const [partId, setPartId] = useState('');
  const [rev, setRev] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const part = (parts || []).find((p) => p.id === partId);

  const submit = async () => {
    if (!partId || !rev.trim()) { toast.error('部品と新しいリビジョンを入力してください'); return; }
    setBusy(true);
    try {
      const d = await devApi.createRevision(devId, {
        part_id: partId, drawing_no: part?.drawing_no || null,
        drawing_revision: rev.trim(), note: note.trim() || null });
      toast.success('改訂を作成しました（旧版は履歴に残っています）');
      setRev(''); setNote('');
      onOpenChange(false); onDone?.(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || '改訂を作成できませんでした');
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white" data-testid="dev-revision-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">改訂を作成</DialogTitle>
          <DialogDescription className="text-sm">
            図面リビジョンを新しくします。旧版と、その版で作った試作・結果は履歴として残ります。
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold text-zinc-700">対象の部品</Label>
            <Select value={partId} onValueChange={setPartId}>
              <SelectTrigger data-testid="dev-revision-part">
                <SelectValue placeholder="選択してください" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {(parts || []).map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}{p.drawing_no ? `（${p.drawing_no} ${p.drawing_revision || ''}）` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-700">新しいリビジョン</Label>
            <Input value={rev} onChange={(e) => setRev(e.target.value)} placeholder="例: Rev C"
              data-testid="dev-revision-value" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-700">変更理由（任意）</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="例: 取付穴の位置を修正" data-testid="dev-revision-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>閉じる</Button>
          <Button onClick={submit} disabled={busy} data-testid="dev-revision-submit">
            {busy ? '作成中…' : '改訂を作成する'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const InstallPartsDialogTestId = 'dev-install-dialog';
